import api from '../../services/apiClient';

/**
 * Fetches a paginated list of client partners.
 * @param {object} params - The query parameters for the request.
 * @param {number} params.page - The page number to fetch.
 * @param {number} params.pageSize - The number of items per page.
 * @returns {Promise<object>} The response from the API containing the list of partners.
 */
export const getClientPartners = (params) => {
  return api.get('/client-partners', { params });
};

/**
 * Fetches client partners by warehouse ID.
 * @param {string} warehouseId - The ID of the warehouse to filter by.
 * @returns {Promise<object>} The response from the API.
 */
export const getClientPartnersByWarehouse = (warehouseId) => {
  return api.get(`/client-partners/warehouse/${warehouseId}`);
};

/**
 * Fetches client partners by status.
 * @param {number} status - The status to filter by (e.g., 0 or 1).
 * @returns {Promise<object>} The response from the API.
 */
export const getClientPartnersByStatus = (status) => {
  return api.get(`/client-partners/status/${status}`);
};

/**
 * Fetches client partners by client ID.
 * @param {string} clientId - The ID of the client to filter by.
 * @returns {Promise<object>} The response from the API.
 */
export const getClientPartnersByClientId = (clientId) => {
  return api.get(`/client-partners/client/${clientId}`);
};

/**
 * Updates an existing client partner.
 * @param {number} id - The ID of the client partner to update.
 * @param {object} partnerData - The updated data for the client partner.
 * @returns {Promise<object>} The response from the API.
 */
export const updateClientPartner = (id, partnerData) => {
  return api.put(`/client-partners/${id}`, partnerData);
};

/**
 * Creates a new client partner.
 * @param {object} partnerData - The data for the new client partner.
 * @param {string} partnerData.clientId - The client's ID.
 * @param {string} partnerData.warehouseId - The warehouse's ID.
 * @param {string} partnerData.email - The partner's email.
 * @param {number} [partnerData.status=1] - The status of the partner. Defaults to 1.
 * @returns {Promise<object>} The response from the API.
 */
export const createClientPartner = ({ clientId, warehouseId, email, status = 1 }) => {
  const payload = {
    clientId,
    warehouseId,
    email,
    status,
  };
  return api.post('/client-partners', payload);
};

